import { addHours, addMinutes, parse } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { Intensity } from '../types/exercise.type'
import { TrainingSession, TrainingSessionStep } from '../types/training-session.type'

const getDefaultSteps = (): TrainingSessionStep[] => [
    {
        title: 'Échauffement',
        description: 'Courir 5 tours, puis 5 tours en faisant des gammes sur les largeurs',
        startTime: '19:00',
        endTime: '19:20',
    },
    {
        title: 'Physique',
        description: 'À définir',
        startTime: '19:20',
        endTime: '19:40',
    },
    {
        title: 'Exercice Technique de déplacement',
        description: 'À définir',
        startTime: '19:40',
        endTime: '20:10',
    },
    {
        title: 'Échauffement raquette',
        description: '1 min de drive<br>1 min de dégagé<br>1 min de filet<br>2 min 3 longs 2 courts',
        startTime: '20:10',
        endTime: '20:30',
    },
    {
        title: 'Exercice Technique raquette',
        description: 'À définir',
        startTime: '20:30',
        endTime: '21:00',
    },
    {
        title: 'Jeu libre',
        description: '',
        startTime: '21:00',
        endTime: '22:00',
    },
]

export interface TrainingSessionFormData {
    id?: number
    title: string
    description?: string
    intensity: Intensity
    steps: TrainingSessionStep[]
    groupId?: number
    order: number
    createdAt?: string
    updatedAt?: string
}

const defaultValues: TrainingSessionFormData = {
    title: 'A définir',
    description: 'A définir',
    intensity: Intensity.MEDIUM,
    order: 1,
    steps: getDefaultSteps(),
}

const formatSessionSteps = (steps: TrainingSessionStep[]): TrainingSessionStep[] => {
    return steps.map((step, index) => {
        const prevStep = index > 0 ? steps[index - 1] : null
        const defaultStartTime = prevStep
            ? addHours(parse(prevStep.endTime, 'HH:mm', new Date()), 1).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
              })
            : '09:00'

        return {
            id: step.id,
            title: step.title || `Étape ${index + 1}`,
            description: step.description !== undefined ? step.description : 'À définir',
            startTime: step.startTime || defaultStartTime,
            endTime:
                step.endTime ||
                addMinutes(parse(defaultStartTime, 'HH:mm', new Date()), 60).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
            groupId: step.groupId,
            exerciseId: step.exerciseId,
        }
    })
}

export const useTrainingSessionForm = (session: TrainingSession | null) => {
    const [formData, setFormData] = useState<TrainingSessionFormData>(() => {
        if (!session) return { ...defaultValues }

        const formattedSteps = session.steps?.length > 0 ? formatSessionSteps(session.steps) : [...defaultValues.steps]

        return {
            id: session.id,
            title: session.title || defaultValues.title,
            // Utiliser la description de la session si elle existe, sinon la valeur par défaut
            description: session.description !== undefined ? session.description : defaultValues.description,
            intensity: session.intensity || defaultValues.intensity,
            order: session.order || defaultValues.order,
            groupId: session.groupId ? +session.groupId : undefined,
            steps: formattedSteps,
            createdAt: session.createdAt,
        }
    })

    // Réinitialiser le formulaire quand la session change
    useEffect(() => {
        if (!session) {
            setFormData({ ...defaultValues })
            return
        }

        const formattedSteps = session.steps?.length > 0 ? formatSessionSteps(session.steps) : [...defaultValues.steps]

        setFormData({
            id: session.id,
            title: session.title || defaultValues.title,
            description: session.description !== undefined ? session.description : defaultValues.description,
            intensity: session.intensity || defaultValues.intensity,
            order: session.order || defaultValues.order,
            groupId: session.groupId ? +session.groupId : undefined,
            steps: formattedSteps,
            createdAt: session.createdAt,
            updatedAt: session.updatedAt,
        })
    }, [session])

    const resetForm = useCallback(
        (newSession: TrainingSession | null = null) => {
            const sessionToUse = newSession || session

            if (!sessionToUse) {
                setFormData({ ...defaultValues })
                return
            }

            const formattedSteps =
                sessionToUse.steps?.length > 0 ? formatSessionSteps(sessionToUse.steps) : [...defaultValues.steps]

            setFormData({
                id: sessionToUse.id,
                title: sessionToUse.title || defaultValues.title,
                // Toujours utiliser la description de la session si elle existe, même si c'est une chaîne vide
                description:
                    sessionToUse.description !== undefined ? sessionToUse.description : defaultValues.description,
                intensity: sessionToUse.intensity || defaultValues.intensity,
                order: sessionToUse.order || defaultValues.order,
                groupId: sessionToUse.groupId ? +sessionToUse.groupId : undefined,
                steps: formattedSteps,
                createdAt: sessionToUse.createdAt,
                updatedAt: sessionToUse.updatedAt,
            })
        },
        [session]
    )

    return {
        formData,
        setFormData,
        resetForm,
        defaultValues,
    }
}

export default useTrainingSessionForm
